import React from "react";
import {
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaMapMarkerAlt,
  FaTelegramPlane,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import { scrollToSection } from "../utils/scrollToSection.ts";

type FooterProps = {
  sectionId?: string;
};

const quickLinks = [
  { label: "Como Funciona", id: "comoFunciona" },
  { label: "Sinais", id: "sinais" },
  { label: "Resultados", id: "resultados" },
  { label: "Planos", id: "precos" },
  { label: "FAQ", id: "faq" },
];

const supportLinks = [
  { label: "Contacto", href: "#" },
  { label: "Suporte Telegram", href: "#" },
  { label: "Centro de Ajuda", href: "#" },
  { label: "FAQ", href: "#" },
];

const legalLinks = [
  { label: "Termos de Uso", href: "#" },
  { label: "Política de Privacidade", href: "#" },
  { label: "Política de Reembolso", href: "#" },
];

const Footer = ({ sectionId }: FooterProps) => {
  return (
    <footer className={`bg-[#03120e] ${sectionId ? "scroll-mt-16" : ""}`}>
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-3 rounded-full bg-[#0a2c24]/80 px-3 py-2">
              <img
                src="/BB-icon.png"
                alt=""
                className="h-10 w-auto object-contain"
              />
              <p className="text-sm font-semibold text-white">
                Binary<span className="text-[#0dc77e]">Bucks</span>
              </p>
            </div>

            <p className="max-w-sm text-sm leading-relaxed text-white/[0.72]">
              Plataforma de sinais de opções binárias impulsionada por Inteligência Artificial. Tecnologia avançada ao serviço do seu trading.
            </p>

            <div className="flex items-center gap-5 text-lg text-white/[0.72] [&_svg]:h-[18px] [&_svg]:w-[18px]">
              <a
                href="https://www.facebook.com/share/1A3baJ9aQA/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-[#0dc77e]"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>
              <a
                href="https://www.instagram.com/binary_bucks?igsh=dHJjMzZscGVkZjM4"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-[#0dc77e]"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>
              <a
                href="https://www.youtube.com/channel/UCbShZ-lrzxGBEQFJUSgRMvQ"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-[#0dc77e]"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>
              <a
                href="https://t.me/binarybucks"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-[#0dc77e]"
                aria-label="Telegram"
              >
                <FaTelegramPlane />
              </a>
              <a
                href="https://www.tiktok.com/@binarybucks"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-[#0dc77e]"
                aria-label="TikTok"
              >
                <FaTiktok />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-white/70">
              Links Rápidos
            </h3>
            <ul className="mt-5 space-y-3 text-sm text-white/[0.72]">
              {quickLinks.map((item) => (
                <li key={item.label}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    className="text-left transition hover:text-[#0dc77e]"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-white/70">
              Suporte
            </h3>
            <ul className="mt-5 space-y-3 text-sm text-white/[0.72]">
              {supportLinks.map((item) => (
                <li key={item.label}>
                  <a href={item.href} className="transition hover:text-[#0dc77e]">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-white/70">
              Legal
            </h3>
            <ul className="mt-5 space-y-3 text-sm text-white/[0.72]">
              {legalLinks.map((item) => (
                <li key={item.label}>
                  <a href={item.href} className="transition hover:text-[#0dc77e]">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[2.2fr_1fr]">
          <div>
            <h4 className="text-sm font-bold text-white">Newsletter</h4>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/[0.70]">
              Receba novidades, dicas e atualizações da plataforma.
            </p>
            <form className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="sr-only" htmlFor="footer-email">
                Endereço de e-mail
              </label>
              <input
                id="footer-email"
                type="email"
                placeholder="o.seu@email.com"
                className="min-w-0 flex-1 rounded-[18px] border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#0dc77e] focus:outline-none focus:ring-2 focus:ring-[#0dc77e]/20"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-[18px] bg-gradient-to-r from-[#0dc77e] to-[#05b277] px-6 py-3 text-sm font-semibold text-slate-950 transition hover:brightness-110"
              >
                Subscrever
              </button>
            </form>
          </div>

          <div className="footer-risk-card rounded-[24px] bg-white/5 p-6 text-sm text-white/[0.80] shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
            <div className="inline-flex items-center gap-3 rounded-full bg-[#ffcc00]/10 px-3 py-2 text-[#ffcc00]">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm.75 5.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM11 11h2v6h-2v-6z" />
              </svg>
              <span className="font-semibold text-white">Aviso de Risco</span>
            </div>
            <p className="mt-4 leading-relaxed text-white/[0.80]">
              As opções binárias envolvem elevado risco e podem resultar na perda total do capital investido. Os sinais fornecidos pela Binary Bucks têm caráter informativo e não constituem aconselhamento financeiro. Negocie apenas com capital que possa perder.
            </p>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6 text-sm text-white/[0.60]">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <span>© {new Date().getFullYear()} Binary Bucks. Todos os direitos reservados.</span>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
              <a href="mailto:info@binarybucks.com" className="inline-flex items-center gap-2 text-white/[0.72] transition hover:text-[#0dc77e]">
                <FaEnvelope /> info@binarybucks.com
              </a>
              <span className="inline-flex items-center gap-2 text-white/[0.72]">
                <FaMapMarkerAlt /> Maputo, Moçambique
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
